import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, AlertTriangle, ClipboardCheck, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHECK_ITEMS = [
  ['engine_oil', 'Engine oil'],
  ['power_steering_fluid', 'Power steering fluid'],
  ['washer_fluid', 'Washer fluid'],
  ['brake_fluid', 'Brake fluid'],
  ['brake_lights', 'Brake lights'],
  ['turn_signals', 'Turn signals'],
  ['headlights', 'Headlights'],
];

const emptyCheck = () => ({
  mileage: '', engine_oil: false, power_steering_fluid: false, washer_fluid: false,
  brake_fluid: false, brake_lights: false, turn_signals: false, headlights: false, notes: '',
});

const fmtMi = (n) => (n === null || n === undefined || n === '') ? '—' : `${Number(n).toLocaleString()} mi`;

// Weekly Monday vehicle check — shows on the tech's My Field Schedule when this
// week's check for their assigned truck isn't done yet. Reuses db.vehicles.
const VehicleCheckCard = () => {
  const [state, setState] = useState(null);   // { has_vehicle, check_done, vehicle, week_start }
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCheck());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/my/vehicle-check`, { withCredentials: true });
      setState(data);
    } catch { setState(null); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = () => {
    setForm({ ...emptyCheck(), mileage: state?.vehicle?.current_mileage ?? '' });
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, mileage: form.mileage !== '' ? Number(form.mileage) : null };
      await axios.post(`${API}/my/vehicle-check`, payload, { withCredentials: true });
      toast.success('Weekly vehicle check saved. Thanks!');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the check');
    }
    setSaving(false);
  };

  if (!state || !state.has_vehicle) return null;

  const v = state.vehicle;
  const oilDue = v?.oil_change_due;

  // Nothing to nag about: check done AND no oil alert → stay quiet.
  if (state.check_done && !oilDue) return null;

  return (
    <>
      <div
        data-testid="vehicle-check-card"
        className={`rounded-md border p-3 flex items-center gap-3 flex-wrap ${state.check_done ? 'bg-[#141414] border-[#262626]' : 'bg-[#D4AF37]/10 border-[#D4AF37]'}`}
      >
        <Truck className={`w-5 h-5 flex-shrink-0 ${state.check_done ? 'text-[#A3A3A3]' : 'text-[#D4AF37]'}`} />
        <div className="flex-1 min-w-[180px]">
          <p className="text-white text-sm font-semibold">
            {state.check_done ? 'Vehicle check done this week' : 'Weekly Vehicle Check due'}
            <span className="text-[#A3A3A3] font-normal"> · {v?.name}</span>
          </p>
          <p className="text-[#A3A3A3] text-xs">
            Current mileage {fmtMi(v?.current_mileage)} · week of {state.week_start}
          </p>
          {oilDue && (
            <p data-testid="vehicle-check-oil-alert" className="text-red-300 text-xs font-bold flex items-center gap-1 mt-0.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Oil Change Due
            </p>
          )}
        </div>
        {!state.check_done ? (
          <Button data-testid="open-vehicle-check" onClick={openDialog} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
            <ClipboardCheck className="w-4 h-4 mr-1" /> Start Check
          </Button>
        ) : (
          <span className="text-emerald-400 text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Done</span>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="vehicle-check-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2"><Truck className="w-5 h-5 text-[#D4AF37]" /> Weekly Vehicle Check</DialogTitle>
            <DialogDescription className="text-[#A3A3A3]">{v?.name} · week of {state.week_start}. Check each item, note anything wrong.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Current Mileage</Label>
              <Input data-testid="check-mileage" type="number" min="0" value={form.mileage} onChange={(e) => setForm({ ...form, mileage: e.target.value })} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Odometer reading" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Checklist (tick each item that's OK)</Label>
              <div className="grid grid-cols-1 gap-1.5">
                {CHECK_ITEMS.map(([key, label]) => (
                  <label key={key} data-testid={`check-item-${key}`} className="flex items-center gap-2.5 bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form[key]}
                      onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                      className="w-4 h-4 accent-[#D4AF37]"
                    />
                    <span className="text-white text-sm">Check {label.toLowerCase()}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Notes if something is wrong</Label>
              <Textarea data-testid="check-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="e.g. Washer fluid low, brake light out…" />
            </div>
            <div className="flex gap-2 pt-1">
              <Button data-testid="submit-vehicle-check" onClick={submit} disabled={saving} className="flex-1 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">{saving ? 'Saving…' : 'Save Check'}</Button>
              <Button variant="outline" onClick={() => setOpen(false)} className="border-[#262626] text-[#A3A3A3]">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VehicleCheckCard;
